import React from "react";
import { Link } from "react-router-dom";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { BannerFeedback } from "../BannerFeedback";
import { Button } from "../Button";
import { FloatContainer } from "../FloatContainer";
import "./NegotiationBalance.css";

export type TermsAcceptanceModalProps = {
  /** The version Payments requires and records (terms.version). */
  version: string;
  /** /termos publishes this version (isNegotiationBalanceTermsPublished). */
  published: boolean;
  /** `org_payments` create: only a payments manager accepts for the company. */
  canAccept: boolean;
  accepting?: boolean;
  /** Application-authored copy only. */
  error?: string;
  /** Initial state of the acceptance checkbox. */
  defaultChecked?: boolean;
  onAccept: () => void;
  onClose: () => void;
};

const checkboxSX = {
  color: "var(--neutral-11)",
  "&.Mui-checked": { color: "var(--brand-9)" },
};

const checkboxLabelSX = {
  alignItems: "flex-start",
  marginLeft: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "13px",
    color: "var(--neutral-11)",
    paddingTop: "var(--spacing-md)",
  },
};

/**
 * The company accepts the Saldo de Negociações conditions once per version,
 * before its first top-up; Payments records who accepted which version.
 *
 * It is offered only for a version /termos publishes and only to a payments
 * manager. While the conditions are unpublished (the owner has not released
 * them yet) or the collaborator cannot accept, it says so and offers nothing.
 * @story args { version: "2026-11-negociacoes-v1", published: true, canAccept: true }
 * @story state Unpublished { published: false }
 * @story state WithoutPermission { canAccept: false }
 * @story state Accepting { defaultChecked: true, accepting: true }
 * @story state Failed { defaultChecked: true, error: "Não foi possível registrar o aceite agora. Tente de novo em instantes." }
 */
export function TermsAcceptanceModal({
  version,
  published,
  canAccept,
  accepting = false,
  error,
  defaultChecked = false,
  onAccept,
  onClose,
}: TermsAcceptanceModalProps) {
  const [checked, setChecked] = React.useState(defaultChecked);
  return (
    <FloatContainer className="negotiationBalanceModal" close={onClose}>
      <div className="negotiationBalanceModalBody" data-testid="negotiation-terms-modal">
        <h3 className="negotiationBalanceModalTitle">Condições do Saldo de Negociações</h3>
        {!published ? (
          <>
            <p className="negotiationBalanceText" data-testid="negotiation-terms-in-review">
              As condições do Saldo de Negociações estão em revisão.
            </p>
            <p className="negotiationBalanceHint">
              As recargas ficam disponíveis quando as condições forem publicadas nos Termos de Uso.
            </p>
          </>
        ) : !canAccept ? (
          <p className="negotiationBalanceText" data-testid="negotiation-terms-ask-manager">
            Peça a quem gerencia pagamentos da empresa para aceitar as condições na aba Pagamentos.
          </p>
        ) : (
          <>
            <BannerFeedback type="error" open={Boolean(error)} message={error ?? ""} />
            <p className="negotiationBalanceText">
              Para abrir o saldo e fazer recargas, a empresa aceita as condições do Saldo de
              Negociações publicadas nos{" "}
              <Link to="/termos" target="_blank" rel="noopener noreferrer">
                Termos de Uso
              </Link>{" "}
              (versão {version}). Só quem gerencia pagamentos aceita, e o aceite fica registrado com
              a versão.
            </p>
            <FormControlLabel
              sx={checkboxLabelSX}
              control={
                <Checkbox
                  sx={checkboxSX}
                  checked={checked}
                  disabled={accepting}
                  onChange={(event) => setChecked(event.target.checked)}
                />
              }
              label={`Li e aceito as condições do Saldo de Negociações (versão ${version})`}
            />
            <div className="negotiationBalanceModalActions">
              <Button
                callback={() => {
                  if (checked && !accepting) onAccept();
                }}
                disabled={!checked}
                loading={{ state: accepting, text: "Registrando o aceite" }}
              >
                Aceitar condições
              </Button>
            </div>
          </>
        )}
      </div>
    </FloatContainer>
  );
}
